/**
 * "Faturas a serem geradas" — as faturas residuais de um pagamento parcial (AF-4097).
 *
 * Mora nas duas telas que marcam pagamento: o modal "Sinalizar pagamento" (Títulos) e o card de cada
 * título no step 3 da Identificação. Mostra a lista que a aprovação vai gerar e abre o
 * `NewInvoiceModal` em modo configurar. Quem decide o que entra na lista (pré-configuração,
 * vazia, editada) é `services/residualInvoices`; aqui só se mostra e se devolve o que o usuário fez.
 */
import { useState } from 'react';
import { FaCog, FaTrash } from 'react-icons/fa';

import NewInvoiceModal from './NewInvoiceModal';
import { ResidualInvoiceSetup } from '../services/billingApi';
import { residualSumWarning } from '../services/residualInvoices';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';

interface Props {
    /** a lista que vai na marcação (já resolvida pelo `effectiveResidualInvoices`) */
    value: ResidualInvoiceSetup[];
    onChange: (next: ResidualInvoiceSetup[]) => void;
    /** a fatura anterior: os defaults do formulário (vencimento, cobrança, boleto) */
    previousInvoice: any;
    charge: any;
    /** o saldo do título depois deste pagamento */
    balance: number;
    /** denominador da fatura do parcial: as residuais dividem essa fração */
    partialDenominator: number;
}

const ResidualInvoicesField: React.FC<Props> = ({
    value, onChange, previousInvoice, charge, balance, partialDenominator,
}) => {
    const [configuring, setConfiguring] = useState(false);
    const denominator = partialDenominator * Math.max(1, value.length);
    const sumWarning = residualSumWarning(value, balance);

    return (
        <div data-testid="residual-invoices" className="text-xs">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Faturas a serem geradas:
            </p>
            {value.length === 0 ? (
                <p className="text-gray-500">nenhuma fatura configurada</p>
            ) : (
                <ul className="space-y-1">
                    {value.map((setup, i) => (
                        <li key={i} className="flex items-center justify-between gap-2">
                            <span>
                                {i + 1}/{denominator} · {formatBRL(setup.value)}
                                {setup.due_date && <> · vence {formatDateBR(setup.due_date)}</>}
                                {' · '}{setup.collect ? 'em cobrança' : 'sem cobrança'}
                                {setup.with_boleto && ' · boleto'}
                            </span>
                            <button type="button" aria-label={`Remover fatura residual ${i + 1}`}
                                className="text-gray-400 hover:text-red-600"
                                onClick={() => onChange(value.filter((_, j) => j !== i))}>
                                <FaTrash />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {sumWarning && (
                <p data-testid="residual-sum-warning" className="mt-1 text-amber-600">
                    Soma {formatBRL(sumWarning.sum)} — {sumWarning.difference > 0 ? 'acima' : 'abaixo'}
                    {' '}do saldo do título em {formatBRL(Math.abs(sumWarning.difference))}
                </p>
            )}
            <button type="button" disabled={!previousInvoice}
                onClick={() => setConfiguring(true)}
                className="mt-1 px-2 py-1 bg-blue-50 text-blue-700 dark:bg-slate-700 dark:text-blue-300 rounded inline-flex items-center gap-1 hover:bg-blue-100 disabled:opacity-50">
                <FaCog /> Configurar faturas
            </button>
            {configuring && previousInvoice && (
                <NewInvoiceModal
                    invoice={previousInvoice}
                    charge={charge}
                    initialItems={value}
                    baseAmount={balance}
                    baseDenominator={partialDenominator}
                    zIndexClass="z-[70]"
                    onConfirm={onChange}
                    onClose={() => setConfiguring(false)}
                    onSuccess={() => setConfiguring(false)}
                />
            )}
        </div>
    );
};

export default ResidualInvoicesField;
